import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@project/ui";
import { Button, Icon } from "@project/ui";
import { UserAvatarImage } from "@apps/dashboard/components/UserAvatarImage";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import {
  fetchDashboardSummary,
  fetchFinancialProfile,
  homeExpenseQueryKeys,
  updateHouseMember,
} from "@apps/dashboard/lib/home-expense-api";
import type { HouseMemberWithUser } from "@apps/dashboard/lib/home-expense-types";
import { formatMinorAsCurrency } from "@apps/dashboard/lib/format-money";
import { dashboardPaths } from "@apps/dashboard/lib/routes";
import { ApiRequestError } from "@apps/dashboard/lib/api-fetch";

function equalBps(n: number): number[] {
  if (n <= 0) return [];
  const base = Math.floor(10000 / n);
  const arr = Array.from({ length: n }, () => base);
  let r = 10000 - base * n;
  for (let i = 0; i < r; i++) {
    const cur = arr[i];
    if (cur !== undefined) arr[i] = cur + 1;
  }
  return arr;
}

function initBpsFromMembers(sorted: HouseMemberWithUser[]): Record<string, number> {
  const n = sorted.length;
  if (n === 0) return {};
  const ids = sorted.map((m) => m.userId);
  const allHave = sorted.every(
    (m) => m.defaultSplitBps != null && m.defaultSplitBps >= 0,
  );
  if (allHave) {
    const sum = sorted.reduce((s, m) => s + (m.defaultSplitBps as number), 0);
    if (sum === 10000) {
      return Object.fromEntries(
        ids.map((id, i) => {
          const row = sorted[i];
          return [id, (row?.defaultSplitBps ?? 0) as number];
        }),
      );
    }
  }
  const arr = equalBps(n);
  return Object.fromEntries(ids.map((id, i) => [id, arr[i]!]));
}

function applyFlexRemainder(
  map: Record<string, number>,
  orderedIds: string[],
): Record<string, number> {
  if (orderedIds.length === 0) return map;
  const flexId = orderedIds[orderedIds.length - 1]!;
  const fixedIds = orderedIds.slice(0, -1);
  const sumFixed = fixedIds.reduce((s, id) => s + (map[id] ?? 0), 0);
  const flex = Math.max(0, Math.min(10000, 10000 - sumFixed));
  return { ...map, [flexId]: flex };
}

function roleLabel(
  t: (k: string) => string,
  role: string,
): string {
  if (role === "owner") return t("dashboardUi.members.roleOwner");
  if (role === "admin") return t("dashboardUi.members.roleAdmin");
  if (role === "member") return t("dashboardUi.members.roleMember");
  return role;
}

function memberInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) {
    const p = parts[0];
    return p ? p.slice(0, 2).toUpperCase() : "?";
  }
  const first = parts[0];
  const last = parts[parts.length - 1];
  if (!first?.[0] || !last?.[0]) return "?";
  return (first[0] + last[0]).toUpperCase();
}

export interface MembersSplitConfigSectionProps {
  houseId: string;
  members: HouseMemberWithUser[];
  canEdit: boolean;
  currentUserId?: string;
}

export function MembersSplitConfigSection({
  houseId,
  members,
  canEdit,
  currentUserId,
}: MembersSplitConfigSectionProps): JSX.Element | null {
  const { t, locale } = useI18n();
  const queryClient = useQueryClient();

  const sortedMembers = useMemo(
    () => [...members].sort((a, b) => a.joinedAt.localeCompare(b.joinedAt)),
    [members],
  );

  const orderedIds = useMemo(
    () => sortedMembers.map((m) => m.userId),
    [sortedMembers],
  );

  const initSignature = sortedMembers
    .map((m) => `${m.userId}:${m.defaultSplitBps ?? ""}`)
    .join("|");

  const [bpsMap, setBpsMap] = useState<Record<string, number> | null>(null);
  const [splitError, setSplitError] = useState<string | null>(null);
  const [splitSuccess, setSplitSuccess] = useState(false);

  useEffect(() => {
    if (sortedMembers.length === 0) {
      setBpsMap(null);
      return;
    }
    const ids = sortedMembers.map((m) => m.userId);
    const base = initBpsFromMembers(sortedMembers);
    setBpsMap(applyFlexRemainder(base, ids));
  }, [initSignature]);

  const financialQuery = useQuery({
    queryKey: homeExpenseQueryKeys.financialProfile(locale, houseId),
    queryFn: () => fetchFinancialProfile(locale, houseId),
  });

  /** Alineado con `DashboardService.summary`: tope del perfil o total planificado del mes. */
  const summaryQuery = useQuery({
    queryKey: homeExpenseQueryKeys.dashboardSummary(locale, houseId),
    queryFn: () => fetchDashboardSummary(locale, houseId),
  });

  const saveSplitMutation = useMutation({
    mutationFn: async () => {
      if (!bpsMap) throw new Error("no draft");
      await Promise.all(
        orderedIds.map((targetUserId) =>
          updateHouseMember(locale, houseId, targetUserId, {
            defaultSplitBps: bpsMap[targetUserId] ?? 0,
          }),
        ),
      );
    },
    onSuccess: () => {
      setSplitError(null);
      setSplitSuccess(true);
      void queryClient.invalidateQueries({
        queryKey: ["houses", houseId, "members"],
      });
    },
    onError: (err: unknown) => {
      const msg =
        err instanceof ApiRequestError
          ? err.message
          : t("dashboardUi.auth.errors.generic");
      setSplitError(msg);
    },
  });

  useEffect(() => {
    if (!splitSuccess) return;
    const id = window.setTimeout(() => setSplitSuccess(false), 4000);
    return () => window.clearTimeout(id);
  }, [splitSuccess]);

  if (sortedMembers.length === 0 || !bpsMap) {
    return null;
  }

  const flexId = orderedIds[orderedIds.length - 1]!;
  const capProfile = financialQuery.data?.monthlyBudgetCapMinor ?? null;
  const plannedTotal = summaryQuery.data?.plannedBudgetMinor ?? 0;
  const effectiveBudgetMinor =
    plannedTotal > 0 ? plannedTotal : capProfile != null && capProfile > 0 ? capProfile : null;
  const hasBudget = effectiveBudgetMinor != null && effectiveBudgetMinor > 0;
  const currency = summaryQuery.data?.currency ?? financialQuery.data?.currency ?? "EUR";
  const budgetBlockLoading = financialQuery.isLoading || summaryQuery.isLoading;
  const budgetBlockError = financialQuery.isError && summaryQuery.isError;

  const setSliderBps = (uid: string, rawNext: number) => {
    if (uid === flexId || !canEdit) return;
    const othersSum = orderedIds
      .filter((id) => id !== flexId && id !== uid)
      .reduce((s, id) => s + (bpsMap[id] ?? 0), 0);
    const maxForUid = Math.max(0, 10000 - othersSum);
    const v = Math.min(maxForUid, Math.max(0, rawNext));
    const next = { ...bpsMap, [uid]: v };
    setBpsMap(applyFlexRemainder(next, orderedIds));
  };

  const totalPercentRounded = 100;

  return (
    <section
      className="mt-8 rounded-2xl bg-surface-container-low p-6 outline outline-1 outline-outline-variant/15"
      data-tour="members-split-section"
    >
      <h2 className="font-headline text-sm font-bold text-on-surface">
        {t("dashboardUi.members.splitSectionTitle")}
      </h2>
      <p className="mt-2 text-body-md text-on-surface-variant">
        {t("dashboardUi.members.splitSectionHelp")}
      </p>

      {!canEdit ? (
        <p className="mt-4 rounded-xl bg-surface-container/80 px-4 py-3 text-body-md text-on-surface-variant">
          {t("dashboardUi.members.splitReadOnlyHint")}
        </p>
      ) : null}

      {budgetBlockLoading ? (
        <p className="mt-4 text-body-md text-on-surface-variant">{t("common.loading")}</p>
      ) : budgetBlockError ? (
        <div className="mt-4 rounded-xl border border-error/30 bg-error/5 px-4 py-3">
          <p className="text-body-md text-on-surface">{t("dashboardUi.states.errorHint")}</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => {
              void financialQuery.refetch();
              void summaryQuery.refetch();
            }}
          >
            {t("dashboardUi.states.retry")}
          </Button>
        </div>
      ) : !hasBudget ? (
        <div className="mt-4 rounded-xl border border-outline-variant/20 bg-surface px-4 py-3">
          <p className="text-body-md text-on-surface-variant">
            {t("dashboardUi.members.splitNoBudgetHint")}
          </p>
          <Button variant="outline" size="sm" className="mt-3" asChild>
            <Link to={dashboardPaths.houseBudget(houseId)}>
              {t("dashboardUi.members.splitOpenBudgetLink")}
            </Link>
          </Button>
        </div>
      ) : (
        <div className="mt-4 rounded-xl bg-primary-container/15 px-4 py-3">
          <p className="text-label-md font-semibold uppercase tracking-wide text-primary">
            {t("dashboardUi.members.splitBudgetEyebrow")}
          </p>
          <p className="mt-1 font-headline text-lg font-bold text-on-surface">
            {formatMinorAsCurrency(effectiveBudgetMinor!, currency, locale)}
          </p>
        </div>
      )}

      <ul className="mt-6 flex flex-col gap-6">
        {sortedMembers.map((m) => {
          const bps = bpsMap[m.userId] ?? 0;
          const pct = Math.round(bps / 100);
          const isFlex = m.userId === flexId;
          const isYou = currentUserId != null && m.userId === currentUserId;
          const shareMinor =
            hasBudget && effectiveBudgetMinor != null
              ? Math.round((effectiveBudgetMinor * bps) / 10000)
              : null;

          return (
            <li
              key={m.id}
              className="rounded-xl border border-outline-variant/15 bg-surface-container-lowest/40 p-4"
            >
              <div className="flex flex-wrap items-center gap-3">
                <UserAvatarImage
                  rawImage={m.user.image}
                  className="size-11 shrink-0 rounded-full object-cover"
                  fallback={
                    <div
                      className="flex size-11 shrink-0 items-center justify-center rounded-full bg-primary/15 font-label text-label-md font-bold text-primary"
                      aria-hidden
                    >
                      {memberInitials(m.user.name)}
                    </div>
                  }
                />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-on-surface">
                    {m.user.name}
                    {isYou ? (
                      <span className="ml-2 text-label-md font-normal text-primary">
                        ({t("dashboardUi.members.splitYouBadge")})
                      </span>
                    ) : null}
                  </p>
                  <p className="text-label-md text-on-surface-variant">
                    {t("dashboardUi.members.roleLabel")}: {roleLabel(t, m.role)}
                  </p>
                </div>
                <div className="w-full text-right sm:w-auto sm:min-w-[7rem]">
                  <p className="font-headline text-lg font-bold text-primary">{pct} %</p>
                  {shareMinor != null ? (
                    <p className="text-body-md text-on-surface-variant">
                      {formatMinorAsCurrency(shareMinor, currency, locale)}
                    </p>
                  ) : null}
                </div>
              </div>

              {sortedMembers.length > 1 ? (
                <div className="mt-4">
                  {isFlex ? (
                    <p className="text-label-md text-on-surface-variant">
                      {t("dashboardUi.members.splitRemainderHint")}
                    </p>
                  ) : (
                    <input
                      type="range"
                      min={0}
                      max={10000}
                      step={100}
                      disabled={!canEdit}
                      value={bps}
                      onChange={(e) =>
                        setSliderBps(m.userId, Number.parseInt(e.target.value, 10))
                      }
                      className="h-2 w-full cursor-pointer accent-primary disabled:cursor-not-allowed disabled:opacity-60"
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={pct}
                      aria-label={t("dashboardUi.members.splitSectionTitle")}
                    />
                  )}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>

      <div
        className="mt-6 flex items-center gap-2 rounded-xl bg-primary/15 px-4 py-3 text-body-md font-medium text-primary"
        role="status"
      >
        <Icon name="CheckCircle2" className="size-5 shrink-0" strokeWidth={2} aria-hidden />
        <span>
          {t("dashboardUi.members.splitTotalLine", {
            percent: String(totalPercentRounded),
          })}{" "}
          · {t("dashboardUi.members.splitTotalValid")}
        </span>
      </div>

      {splitError ? (
        <p className="mt-3 text-body-md text-error" role="alert">
          {splitError}
        </p>
      ) : null}
      {splitSuccess ? (
        <p className="mt-3 text-body-md text-primary" role="status">
          {t("dashboardUi.members.splitSaveSuccess")}
        </p>
      ) : null}

      {canEdit ? (
        <Button
          type="button"
          className="mt-6 w-full sm:w-auto"
          disabled={saveSplitMutation.isPending}
          onClick={() => saveSplitMutation.mutate()}
        >
          {saveSplitMutation.isPending
            ? t("dashboardUi.members.splitSaving")
            : t("dashboardUi.members.splitSaveCta")}
        </Button>
      ) : null}
    </section>
  );
}
